<template>
  <div ref="scrollRoot" class="hawksense-landing" @scroll.passive="handleScroll">
    <header class="site-header">
      <div class="brand" @click="scrollTo('home')">
        <div class="brand-mark">硅</div>
        <div class="brand-text">
          <strong>深圳硅纳智慧科技有限公司</strong>
          <span>工业 AI 视觉质检</span>
        </div>
      </div>

      <nav class="nav">
        <button
          v-for="item in navItems"
          :key="item.key"
          class="nav-item"
          :class="{ 'is-active': activeSection === item.key }"
          type="button"
          @click="scrollTo(item.key)"
        >
          {{ item.label }}
        </button>
      </nav>

      <div class="header-actions">
        <button class="ghost-btn" type="button" @click="openMail">预约演示</button>
        <button class="login-btn" type="button" @click="goLogin">登录后台</button>
      </div>
    </header>

    <main class="site-main">
      <section id="home" ref="home" class="hero section-anchor">
        <div class="hero-copy">
          <p class="hero-kicker">TEXTILE VISION OPERATING SYSTEM</p>
          <h1>让纺织质检进入<br>“实时、量化、可追溯”时代</h1>
          <p class="hero-desc">
            从织造现场的边织边检，到验布环节的全幅检测，硅纳智慧通过边缘 AI 与行业模型，
            持续识别布面缺陷、沉淀质量数据，让问题更早发现、更快处置。
          </p>
          <div class="hero-actions">
            <button class="cta-btn cta-btn-primary" type="button" @click="goLogin">进入管理后台</button>
            <button class="cta-btn cta-btn-secondary" type="button" @click="scrollTo('benefits')">查看效益对比</button>
          </div>
          <div class="hero-metric-grid">
            <article v-for="item in heroMetrics" :key="item.label" class="hero-metric">
              <p>{{ item.value }}</p>
              <span>{{ item.label }}</span>
            </article>
          </div>
        </div>

        <div class="hero-panel">
          <div class="panel-tag">Live Inspection</div>
          <div class="panel-rows">
            <div class="panel-row">
              <span>检测状态</span>
              <strong class="success">稳定运行</strong>
            </div>
            <div class="panel-row">
              <span>当前速度</span>
              <strong>102 m/min</strong>
            </div>
            <div class="panel-row">
              <span>识别精度</span>
              <strong>0.2 mm²</strong>
            </div>
            <div class="panel-row">
              <span>在线机台</span>
              <strong>42 台</strong>
            </div>
          </div>
          <div class="panel-wave"></div>
        </div>
      </section>

      <section id="products" ref="products" class="section section-anchor">
        <div class="section-head">
          <p class="section-kicker">Product Portfolio</p>
          <h2>覆盖织造与验布的产品矩阵</h2>
          <p class="section-intro">围绕布面质量形成前置检测与成品复检两道防线，让每一次识别都有现场动作和数据去向。</p>
        </div>
        <div class="product-line-grid">
          <article v-for="item in productLineItems" :key="item.title" class="product-line-card" :class="item.theme">
            <div class="product-line-icon"><Icon :type="item.icon" /></div>
            <div>
              <span class="product-line-label">{{ item.label }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <strong>{{ item.value }}</strong>
            </div>
          </article>
        </div>
      </section>

      <section class="section aiqc-section">
        <div class="aiqc-layout">
          <div class="aiqc-visual" aria-label="AI 质检系统识别到的真实布面缺陷样例">
            <figure class="defect-frame defect-frame-main">
              <img :src="aiqcImages.holes" alt="AI 质检识别的针眼和破洞缺陷布面">
              <figcaption><span></span> 针眼 / 破洞识别样例</figcaption>
            </figure>
            <figure class="defect-frame defect-frame-secondary">
              <img :src="aiqcImages.line" alt="AI 质检识别的经向线状缺陷布面">
              <figcaption><span></span> 经向缺陷识别样例</figcaption>
            </figure>
          </div>

          <div class="aiqc-copy">
            <p class="section-kicker">NEW PRODUCT LINE · AI QUALITY INSPECTION</p>
            <h2>AI 质检系统：把质量防线前移到织造现场</h2>
            <p class="aiqc-lead">
              系统将高速成像、边缘计算、深度学习与现场联动集成到织机旁，
              实现从“事后抽检”到“实时边织边检”的升级。
            </p>
            <div class="aiqc-metrics">
              <div v-for="item in aiqcMetrics" :key="item.label" class="aiqc-metric">
                <strong>{{ item.value }}</strong>
                <span>{{ item.label }}</span>
              </div>
            </div>
            <div class="aiqc-flow">
              <div v-for="(item, index) in aiqcFlow" :key="item.title" class="aiqc-flow-item">
                <span class="flow-index">{{ index + 1 }}</span>
                <div>
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.desc }}</p>
                </div>
              </div>
            </div>
            <div class="aiqc-scope">
              <p><strong>适用布种</strong> 单面平纹、罗纹、双面/罗马、毛圈/卫衣、珠地/网眼</p>
              <p><strong>可检疵点</strong> 纬向缺陷、经向缺陷、破洞、针眼、脏污、异物</p>
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="section-head">
          <p class="section-kicker">Product Advantages</p>
          <h2>HawkSense 产品特点与优势</h2>
        </div>
        <div class="advantage-grid">
          <article v-for="(item, index) in advantageItems" :key="item.title" class="adv-card">
            <span class="adv-no">{{ `0${index + 1}` }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </section>

      <section class="section">
        <div class="section-head">
          <p class="section-kicker">Product Forms</p>
          <h2>核心产品与形态</h2>
        </div>
        <div class="form-grid">
          <article v-for="item in formItems" :key="item.title" class="form-card">
            <div class="form-media">
              <span>{{ item.tag }}</span>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </section>

      <section id="benefits" ref="benefits" class="section section-anchor">
        <div class="section-head">
          <p class="section-kicker">Investment Benefits</p>
          <h2>投资效益对比</h2>
        </div>
        <div class="benefit-grid">
          <article v-for="item in benefitItems" :key="item.title" class="benefit-card">
            <h3>{{ item.title }}</h3>
            <p class="benefit-ai">AI：{{ item.ai }}</p>
            <p class="benefit-human">人工：{{ item.human }}</p>
            <strong>{{ item.summary }}</strong>
          </article>
        </div>
      </section>

      <section id="partners" ref="partners" class="section section-anchor">
        <div class="section-head">
          <p class="section-kicker">Partners</p>
          <h2>行业领先者的共同选择</h2>
        </div>
        <div class="partner-wall">
          <div v-for="item in partnerItems" :key="item" class="partner-item">{{ item }}</div>
        </div>
      </section>

      <section id="about" ref="about" class="section about section-anchor">
        <div class="section-head">
          <p class="section-kicker">Company Profile</p>
          <h2>关于我们</h2>
        </div>
        <div class="about-grid">
          <article class="about-card">
            <h3>公司信息</h3>
            <p>深圳硅纳智慧科技有限公司</p>
            <p>联系地址：深圳市福田区卓越世纪中心1号楼4704A</p>
            <p>企业邮箱：fish@sinano-ai.com</p>
          </article>
          <article class="about-card">
            <h3>全球布局</h3>
            <p>绍兴、杭州、上海、广州、越南、孟加拉等地区持续拓展。</p>
            <p>以产品化与数据化能力，服务纺织制造数字升级。</p>
          </article>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <p>
        © 2026 深圳硅纳智慧科技有限公司 版权所有 |
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备17141449号-2</a>
      </p>
    </footer>
  </div>
</template>

<script>
import aiqcDefectHoles from '@/assets/images/aiqc-defect-holes.jpg'
import aiqcDefectLine from '@/assets/images/aiqc-defect-line.jpg'

export default {
  name: 'LandingPage',
  data () {
    return {
      activeSection: 'home',
      navItems: [
        { key: 'home', label: '首页' },
        { key: 'products', label: '产品中心' },
        { key: 'benefits', label: '设备效益' },
        { key: 'partners', label: '合作伙伴' },
        { key: 'about', label: '关于我们' }
      ],
      productLineItems: [
        {
          label: '验布环节',
          title: 'HawkSense AI 验布平台',
          desc: '面向验布机与成品布检测，以高速度、稳定识别和质量追溯提升验布效率。',
          value: '全幅检测 · 量化验布 · 数据追溯',
          icon: 'md-eye',
          theme: 'is-hawksense'
        },
        {
          label: '织造环节 · 新产品线',
          title: 'AI 质检系统',
          desc: '部署在织机现场，边织边检并联动告警或停机，在瑕疵继续扩大前完成处置。',
          value: '实时检测 · 边缘识别 · 闭环处置',
          icon: 'md-analytics',
          theme: 'is-aiqc'
        }
      ],
      aiqcImages: {
        holes: aiqcDefectHoles,
        line: aiqcDefectLine
      },
      aiqcMetrics: [
        { value: '24h', label: '支持连续检测' },
        { value: '5 类', label: '适用针织布种' },
        { value: '6 类', label: '明确覆盖疵点' }
      ],
      aiqcFlow: [
        { title: '高速抓拍', desc: '工业相机连续采集布面图像，保留关键纹理细节。' },
        { title: '边缘计算', desc: '现场完成降噪与特征提取，处理节拍与生产同步。' },
        { title: 'AI 识别', desc: '定位缺陷类型、尺寸与坐标，形成统一判断标准。' },
        { title: '智能处置', desc: '按规则标记存证、告警或联动停机，沉淀追溯档案。' }
      ],
      heroMetrics: [
        { value: '90%+', label: '缺陷检出率' },
        { value: '50-120m/min', label: '稳定验布速度' },
        { value: '24h', label: '连续运行能力' }
      ],
      advantageItems: [
        { title: '10万+ 超低成本', desc: '采用边缘计算芯片，降低部署与运行门槛。' },
        { title: '90%+ 高精度识别', desc: '自研深度学习模型，持续优化缺陷识别准确率。' },
        { title: '60米/分+ 高效率', desc: '验布速度稳定，单人可同时管理多台设备。' },
        { title: 'OTA 持续进化', desc: '算法支持在线升级，能力随业务不断增长。' },
        { title: '一键部署上线', desc: '软硬件一体化方案，24小时内完成落地。' }
      ],
      formItems: [
        { tag: 'Retrofit', title: '加装落地产品', desc: '包含无动力与动力加装两种方案。' },
        { tag: 'Integrated', title: '整机落地产品', desc: '兼容针织与机织面料，适配多场景。' },
        { tag: 'Coverage', title: '机型覆盖', desc: '散对卷、卷对散、卷对卷机型均可支持。' }
      ],
      benefitItems: [
        { title: '效率提升', ai: '50-120m/min', human: '15-20m/min', summary: 'AI 速度可达人工 3 倍以上' },
        { title: '精度提升', ai: '检出率 90%+', human: '检出率 60-70%', summary: '显著降低漏检与误检风险' },
        { title: '稳定性', ai: '24 小时连续运行', human: '依赖人工状态波动', summary: '持续作业能力更强' }
      ],
      partnerItems: ['福建东龙', '互太纺织', '浙江鑫兰', '致兴纺织', '溢达集团', 'SHEIN']
    }
  },
  mounted () {
    window.document.title = '深圳硅纳智慧科技有限公司 | 工业 AI 视觉质检'
    this.$nextTick(() => {
      this.handleScroll()
    })
  },
  methods: {
    goLogin () {
      this.$router.push({ name: 'login' })
    },
    openMail () {
      window.location.href = 'mailto:fish@sinano-ai.com'
    },
    scrollTo (id) {
      const root = this.$refs.scrollRoot
      const target = this.$refs[id]
      if (!root || !target) return
      const top = Math.max(0, target.offsetTop - 84)
      root.scrollTo({ top, behavior: 'smooth' })
    },
    handleScroll () {
      const root = this.$refs.scrollRoot
      if (!root) return

      const currentTop = root.scrollTop + 160
      let current = 'home'

      this.navItems.forEach(item => {
        const section = this.$refs[item.key]
        if (section && section.offsetTop <= currentTop) {
          current = item.key
        }
      })

      this.activeSection = current
    }
  }
}
</script>

<style scoped lang="less">
.hawksense-landing {
  --tone-ink: #102238;
  --tone-main: #18477f;
  --tone-accent: #cf9c37;
  --surface-main: rgba(255, 255, 255, 0.82);
  --surface-soft: rgba(255, 255, 255, 0.72);
  --line-soft: rgba(23, 72, 129, 0.2);

  height: 100%;
  overflow-y: auto;
  color: var(--tone-ink);
  font-family: "Source Han Sans CN", "PingFang SC", "Microsoft YaHei", sans-serif;
  background:
    radial-gradient(circle at 12% 8%, rgba(16, 93, 184, 0.2), transparent 36%),
    radial-gradient(circle at 88% 84%, rgba(207, 156, 55, 0.22), transparent 32%),
    linear-gradient(155deg, #edf3fb 0%, #f8fbff 46%, #eef5ff 100%);
}

.hawksense-landing::before {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  background:
    repeating-linear-gradient(90deg, rgba(30, 72, 118, 0.05) 0, rgba(30, 72, 118, 0.05) 1px, transparent 1px, transparent 26px),
    linear-gradient(120deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0));
}

.site-header,
.site-main,
.site-footer {
  position: relative;
  z-index: 1;
}

.site-header {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 16px 42px;
  border-bottom: 1px solid var(--line-soft);
  background: rgba(247, 251, 255, 0.88);
  backdrop-filter: blur(8px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(150deg, #0d305c, #1c68c3);
  box-shadow: 0 10px 20px rgba(28, 77, 141, 0.28);
}

.brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
}

.brand-text strong {
  font-size: 14px;
  white-space: nowrap;
}

.brand-text span {
  color: #4f6786;
  font-size: 12px;
}

.nav {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(24, 71, 127, 0.14);
}

.nav-item {
  flex: 0 0 auto;
  border: 0;
  border-radius: 999px;
  padding: 7px 12px;
  color: #20446f;
  background: transparent;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.22s ease;
}

.nav-item:hover {
  color: #103969;
  background: rgba(31, 92, 165, 0.14);
}

.nav-item.is-active {
  color: #fff;
  background: linear-gradient(130deg, #18477f, #2e75c2);
  box-shadow: 0 8px 14px rgba(26, 76, 136, 0.26);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ghost-btn,
.login-btn {
  border: 0;
  border-radius: 10px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
}

.ghost-btn {
  color: #17437a;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(24, 71, 127, 0.24);
}

.ghost-btn:hover {
  background: #fff;
}

.login-btn {
  color: #fff;
  background: linear-gradient(130deg, #123967, #2365af);
  box-shadow: 0 10px 16px rgba(17, 53, 95, 0.28);
}

.login-btn:hover {
  transform: translateY(-1px);
}

.site-main {
  padding: 30px 42px 52px;
}

.section {
  margin-bottom: 44px;
}

.hero {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 24px;
  align-items: stretch;
  margin-bottom: 56px;
}

.hero-copy {
  padding: 18px 2px 8px 0;
  animation: rise-in 0.6s ease both;
}

.hero-kicker {
  margin: 0 0 12px;
  color: #2a5c98;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.8px;
  font-family: "IBM Plex Mono", "Consolas", monospace;
}

.hero-copy h1 {
  margin: 0 0 14px;
  color: #0e2a49;
  font-size: clamp(34px, 5vw, 56px);
  line-height: 1.18;
  letter-spacing: 0.5px;
  font-family: "STZhongsong", "Songti SC", "Noto Serif SC", serif;
}

.hero-desc {
  margin: 0;
  max-width: 620px;
  color: #3f5574;
  font-size: 16px;
  line-height: 1.8;
}

.hero-actions {
  display: flex;
  gap: 10px;
  margin-top: 22px;
}

.cta-btn {
  border: 0;
  border-radius: 11px;
  padding: 12px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  transition: all 0.22s ease;
}

.cta-btn-primary {
  color: #fff;
  background: linear-gradient(130deg, #124275, #2a6bb4);
  box-shadow: 0 12px 20px rgba(20, 64, 114, 0.28);
}

.cta-btn-secondary {
  color: #1c4d82;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(30, 78, 131, 0.26);
}

.cta-btn:hover {
  transform: translateY(-1px);
}

.hero-metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 18px;
}

.hero-metric {
  padding: 12px;
  border-radius: 12px;
  border: 1px solid var(--line-soft);
  background: var(--surface-main);
}

.hero-metric p {
  margin: 0;
  color: #143f72;
  font-size: 19px;
  font-weight: 700;
}

.hero-metric span {
  display: block;
  margin-top: 6px;
  color: #4f6786;
  font-size: 12px;
}

.hero-panel {
  position: relative;
  border-radius: 20px;
  padding: 18px;
  overflow: hidden;
  border: 1px solid rgba(209, 161, 74, 0.24);
  background:
    radial-gradient(circle at 78% 8%, rgba(223, 176, 84, 0.22), transparent 36%),
    linear-gradient(145deg, rgba(15, 35, 60, 0.95), rgba(21, 63, 110, 0.94));
  box-shadow: 0 24px 44px rgba(20, 41, 71, 0.28);
  animation: rise-in 0.7s ease both;
}

.panel-tag {
  display: inline-flex;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #bdd7ff;
  font-size: 12px;
}

.panel-rows {
  margin-top: 16px;
}

.panel-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 13px 2px;
  border-bottom: 1px dashed rgba(182, 208, 247, 0.22);
  color: #8eb3e9;
  font-size: 13px;
}

.panel-row strong {
  color: #f4f8ff;
  font-size: 16px;
}

.panel-row .success {
  color: #88efc2;
}

.panel-wave {
  position: absolute;
  right: -60px;
  bottom: -80px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(121, 164, 232, 0.32), transparent 70%);
  animation: pulse-wave 3.2s ease-in-out infinite;
}

.section-head {
  margin-bottom: 18px;
}

.section-kicker {
  margin: 0 0 8px;
  color: #2b5f99;
  font-size: 12px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-family: "IBM Plex Mono", "Consolas", monospace;
}

.section-head h2 {
  margin: 0;
  color: #122e4f;
  font-size: 30px;
  letter-spacing: 0.5px;
  font-family: "STZhongsong", "Songti SC", "Noto Serif SC", serif;
}

.section-intro {
  max-width: 720px;
  margin: 10px 0 0;
  color: #4b6282;
  font-size: 14px;
  line-height: 1.7;
}

.product-line-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.product-line-card {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 16px;
  min-height: 188px;
  padding: 24px;
  border: 1px solid var(--line-soft);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 14px 24px rgba(29, 76, 128, 0.1);
}

.product-line-card.is-aiqc {
  border-color: rgba(34, 92, 74, 0.3);
  background: #f4f8f5;
}

.product-line-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  color: #fff;
  background: #174b82;
  font-size: 24px;
}

.is-aiqc .product-line-icon {
  background: #236a55;
}

.product-line-label {
  display: block;
  margin-bottom: 8px;
  color: #55708f;
  font-size: 12px;
  font-weight: 700;
}

.product-line-card h3 {
  margin: 0 0 9px;
  color: #15395f;
  font-size: 21px;
}

.product-line-card p {
  margin: 0 0 14px;
  color: #4a6484;
  font-size: 14px;
  line-height: 1.7;
}

.product-line-card strong {
  color: #245380;
  font-size: 13px;
}

.is-aiqc h3,
.is-aiqc strong {
  color: #205a48;
}

.aiqc-section {
  margin-right: -42px;
  margin-left: -42px;
  padding: 46px 42px;
  color: #f5f8fa;
  background: #182c31;
}

.aiqc-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(420px, 1.1fr);
  gap: 44px;
  align-items: center;
}

.aiqc-visual {
  position: relative;
  min-height: 500px;
}

.defect-frame {
  position: absolute;
  margin: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  background: #0f1b1f;
  box-shadow: 0 20px 38px rgba(0, 0, 0, 0.28);
}

.defect-frame-main {
  top: 0;
  left: 0;
  width: 76%;
}

.defect-frame-secondary {
  right: 0;
  bottom: 0;
  width: 48%;
}

.defect-frame img {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

.defect-frame figcaption {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 12px;
  color: #dce9e5;
  font-size: 12px;
}

.defect-frame figcaption span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5bd29f;
  box-shadow: 0 0 0 4px rgba(91, 210, 159, 0.12);
}

.aiqc-copy .section-kicker {
  color: #8bd6bb;
}

.aiqc-copy h2 {
  margin: 0;
  color: #fff;
  font-size: 30px;
  line-height: 1.35;
}

.aiqc-lead {
  margin: 14px 0 0;
  color: #b9cac7;
  font-size: 15px;
  line-height: 1.8;
}

.aiqc-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 24px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.aiqc-metric {
  padding: 18px 12px 18px 0;
}

.aiqc-metric + .aiqc-metric {
  padding-left: 16px;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
}

.aiqc-metric strong,
.aiqc-metric span {
  display: block;
}

.aiqc-metric strong {
  color: #70d5af;
  font-size: 24px;
}

.aiqc-metric span {
  margin-top: 5px;
  color: #afc2be;
  font-size: 12px;
}

.aiqc-flow {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 24px;
}

.aiqc-flow-item {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 10px;
}

.flow-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(112, 213, 175, 0.55);
  border-radius: 50%;
  color: #83dfbc;
  font-size: 12px;
  font-weight: 700;
}

.aiqc-flow-item h3 {
  margin: 2px 0 5px;
  color: #f5f8fa;
  font-size: 15px;
}

.aiqc-flow-item p {
  margin: 0;
  color: #aebfbc;
  font-size: 12px;
  line-height: 1.6;
}

.aiqc-scope {
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.aiqc-scope p {
  margin: 7px 0 0;
  color: #b9cac7;
  font-size: 12px;
  line-height: 1.7;
}

.aiqc-scope strong {
  display: inline-block;
  min-width: 68px;
  color: #fff;
}

.advantage-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.adv-card {
  padding: 18px 14px;
  border-radius: 14px;
  border: 1px solid var(--line-soft);
  background: var(--surface-main);
  box-shadow: 0 14px 24px rgba(29, 76, 128, 0.1);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.adv-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 28px rgba(26, 69, 117, 0.16);
}

.adv-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  color: #1e5490;
  font-weight: 700;
  background: rgba(41, 102, 180, 0.12);
}

.adv-card h3 {
  margin: 10px 0 8px;
  font-size: 17px;
  color: #15395f;
}

.adv-card p {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: #46607f;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.form-card {
  border-radius: 16px;
  padding: 14px;
  border: 1px solid var(--line-soft);
  background: var(--surface-main);
  box-shadow: 0 14px 24px rgba(29, 76, 128, 0.1);
}

.form-media {
  height: 170px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #eef5ff;
  font-size: 13px;
  font-family: "IBM Plex Mono", "Consolas", monospace;
  letter-spacing: 0.5px;
  background:
    linear-gradient(130deg, rgba(211, 160, 66, 0.2), rgba(211, 160, 66, 0)),
    linear-gradient(145deg, #14365d, #2b6bb3);
}

.form-card h3 {
  margin: 12px 0 8px;
  color: #15395f;
  font-size: 18px;
}

.form-card p {
  margin: 0;
  color: #4a6484;
  font-size: 13px;
}

.benefit-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.benefit-card {
  padding: 16px;
  border-radius: 14px;
  border: 1px solid var(--line-soft);
  background: var(--surface-main);
}

.benefit-card h3 {
  margin: 0 0 10px;
  color: #15395f;
  font-size: 20px;
}

.benefit-card p {
  margin: 0 0 7px;
  font-size: 14px;
}

.benefit-ai {
  color: #1f5592;
  font-weight: 700;
}

.benefit-human {
  color: #607798;
}

.benefit-card strong {
  display: block;
  margin-top: 10px;
  color: #1c426f;
  font-size: 14px;
}

.partner-wall {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.partner-item {
  border-radius: 12px;
  border: 1px solid var(--line-soft);
  background: rgba(255, 255, 255, 0.8);
  text-align: center;
  padding: 14px;
  color: #234a76;
  font-weight: 700;
}

.about-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.about-card {
  border-radius: 14px;
  border: 1px solid var(--line-soft);
  padding: 16px;
  background: var(--surface-soft);
}

.about-card h3 {
  margin: 0 0 10px;
  color: #173d67;
  font-size: 18px;
}

.about-card p {
  margin: 0 0 8px;
  color: #4b6282;
  line-height: 1.7;
}

.site-footer {
  margin: 0 42px 26px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid rgba(17, 57, 98, 0.2);
  text-align: center;
  color: #d9e6fb;
  background: linear-gradient(145deg, #113357, #1d558e);
}

.site-footer p {
  margin: 0;
  font-size: 13px;
}

.site-footer a {
  color: inherit;
  text-decoration: none;
}

.site-footer a:hover,
.site-footer a:focus-visible {
  color: #fff;
  text-decoration: underline;
}

@keyframes rise-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse-wave {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.62;
  }
  50% {
    transform: scale(1.12);
    opacity: 0.3;
  }
}

@media (max-width: 1240px) {
  .advantage-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 980px) {
  .site-header {
    flex-wrap: wrap;
    padding: 14px 18px;
  }

  .nav {
    order: 3;
    width: 100%;
    overflow-x: auto;
  }

  .site-main {
    padding: 20px 18px 40px;
  }

  .brand-text strong {
    font-size: 13px;
  }

  .hero {
    grid-template-columns: 1fr;
  }

  .hero-metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .form-grid,
  .benefit-grid,
  .about-grid,
  .product-line-grid {
    grid-template-columns: 1fr;
  }

  .aiqc-section {
    margin-right: -18px;
    margin-left: -18px;
    padding: 38px 18px;
  }

  .aiqc-layout {
    grid-template-columns: 1fr;
    gap: 34px;
  }

  .aiqc-visual {
    width: 100%;
    max-width: 620px;
    min-height: 500px;
    margin: 0 auto;
  }

  .partner-wall {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .site-footer {
    margin: 0 18px 22px;
  }
}

@media (max-width: 620px) {
  .site-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: flex-start;
  }

  .brand {
    grid-column-start: 1;
    grid-column-end: 2;
    grid-row-start: 1;
    width: 100%;
    max-width: none;
  }

  .brand-text strong {
    white-space: normal;
    line-height: 1.35;
  }

  .header-actions {
    grid-column-start: 2;
    grid-column-end: 3;
    grid-row-start: 1;
  }

  .nav {
    grid-column-start: 1;
    grid-column-end: 3;
    grid-row-start: 2;
    justify-content: space-between;
  }

  .nav-item {
    padding-right: 8px;
    padding-left: 8px;
    font-size: 12px;
  }

  .ghost-btn {
    display: none;
  }

  .login-btn {
    padding: 9px 11px;
  }

  .product-line-card {
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 12px;
    min-height: 0;
    padding: 18px 16px;
  }

  .product-line-icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .aiqc-visual {
    min-height: 390px;
  }

  .defect-frame-main {
    width: 82%;
  }

  .defect-frame-secondary {
    width: 52%;
  }

  .aiqc-copy h2 {
    font-size: 24px;
  }

  .aiqc-metrics {
    grid-template-columns: 1fr;
  }

  .aiqc-metric,
  .aiqc-metric + .aiqc-metric {
    display: grid;
    grid-template-columns: 88px minmax(0, 1fr);
    align-items: center;
    padding: 12px 0;
    border-left: 0;
  }

  .aiqc-metric + .aiqc-metric {
    border-top: 1px solid rgba(255, 255, 255, 0.12);
  }

  .aiqc-metric span {
    margin-top: 0;
  }

  .aiqc-flow {
    grid-template-columns: 1fr;
  }

  .hero-copy h1 {
    font-size: 32px;
  }

  .hero-desc {
    font-size: 14px;
  }

  .hero-actions {
    flex-direction: column;
  }

  .hero-metric-grid {
    grid-template-columns: 1fr;
  }

  .advantage-grid,
  .partner-wall {
    grid-template-columns: 1fr;
  }

  .section-head h2 {
    font-size: 24px;
  }
}
</style>
